import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { describeAxiosError } from "@/lib/api";
import Brand from "@/components/Brand";
import { ArrowRight, CheckCircle, Lock } from "@phosphor-icons/react";
import PasswordInput from "@/components/PasswordInput";

// Set a new password using a one-time reset token from the emailed link.
export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const nav = useNavigate();

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(null); // null | { role }

    useEffect(() => {
        if (!token) setErr("This reset link is missing its token. Please request a new one.");
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (password.length < 6) {
            setErr("Password must be at least 6 characters.");
            return;
        }
        if (password !== confirm) {
            setErr("Passwords don't match.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/reset-password", { token, password });
            setDone({ role: data.role });
        } catch (ex) {
            setErr(describeAxiosError(ex));
        } finally {
            setLoading(false);
        }
    };

    const targetLogin = done?.role === "candidate" ? "/candidate/login" : "/login";

    return (
        <div className="min-h-screen w-full flex bg-paper" data-testid="reset-password-page">
            <div className="w-full lg:w-2/3 flex items-center justify-center px-6 sm:px-12 py-12 bg-white border-r border-[#E5E5E5]">
                <div className="w-full max-w-md">
                    <div className="mb-12">
                        <Brand size="h-9" />
                    </div>

                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-3">
                        Choose a new password
                    </p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-4">
                        Set a fresh <br />
                        <span className="text-cobalt">password.</span>
                    </h1>
                    <p className="text-[#525252] mb-10 max-w-sm">
                        Once you save, we&apos;ll sign you out of every device. Use the new password to sign in.
                    </p>

                    {done ? (
                        <div
                            data-testid="reset-password-success"
                            className="border border-emerald-300 bg-emerald-50 px-5 py-5"
                        >
                            <div className="flex items-start gap-3 mb-4">
                                <CheckCircle size={22} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                    <p className="font-bold text-emerald-900 mb-1">Password updated.</p>
                                    <p className="text-sm text-emerald-800">
                                        Sign in with your new password to continue.
                                    </p>
                                </div>
                            </div>
                            <button
                                data-testid="reset-password-goto-login"
                                type="button"
                                onClick={() => nav(targetLogin, { replace: true })}
                                className="btn-gradient w-full px-6 py-3 font-bold tracking-wide text-sm flex items-center justify-center gap-2"
                            >
                                Go to sign in <ArrowRight size={16} weight="bold" />
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    New password
                                </label>
                                <PasswordInput
                                    data-testid="reset-password-new-input"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    autoComplete="new-password"
                                    autoFocus
                                    disabled={!token}
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-3 text-base text-ink placeholder-[#A3A3A3] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt disabled:opacity-50"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Confirm new password
                                </label>
                                <PasswordInput
                                    data-testid="reset-password-confirm-input"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    required
                                    minLength={6}
                                    autoComplete="new-password"
                                    disabled={!token}
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-3 text-base text-ink placeholder-[#A3A3A3] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt disabled:opacity-50"
                                />
                            </div>

                            {err && (
                                <div
                                    data-testid="reset-password-error"
                                    className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-4 py-3"
                                >
                                    {err}
                                </div>
                            )}

                            <button
                                data-testid="reset-password-submit-button"
                                disabled={loading || !token}
                                type="submit"
                                className="btn-gradient w-full px-6 py-3.5 font-bold tracking-wide text-sm flex items-center justify-center gap-2"
                            >
                                {loading ? "Saving…" : "Save new password"}{" "}
                                <ArrowRight size={16} weight="bold" />
                            </button>
                        </form>
                    )}

                    <div className="mt-10 pt-6 border-t border-[#E5E5E5] flex flex-col gap-2 text-xs text-[#525252]">
                        <Link
                            to="/forgot-password"
                            data-testid="reset-request-new-link"
                            className="font-bold text-ink hover:text-cobalt"
                        >
                            Request a new reset link →
                        </Link>
                    </div>
                </div>
            </div>

            <div className="hidden lg:flex w-1/3 bg-ink text-white p-12 flex-col justify-between">
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-white/40">
                    Secure by default
                </p>
                <div>
                    <p className="font-heading font-black text-6xl xl:text-7xl leading-[0.95] tracking-tighter">
                        One link. <br />
                        <span className="text-cobalt">One time.</span>
                    </p>
                    <p className="mt-8 text-white/60 max-w-xs flex items-center gap-2">
                        <Lock size={16} weight="bold" className="text-cobalt" />
                        Reset links are single-use and expire in 45 minutes.
                    </p>
                </div>
                <div className="text-xs font-bold uppercase tracking-[0.3em] text-white/40">
                    © {new Date().getFullYear()} — NaukriDaddy.com
                </div>
            </div>
        </div>
    );
}
